// Gestion des Livraisons - Espace Vendeur
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Truck, Package, MapPin, Clock, CheckCircle2, AlertCircle,
  Search, Filter, Eye, Navigation, Phone, ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

// Statuts de livraison
const DELIVERY_STATUSES = {
  preparing: { color: 'bg-blue-100 text-blue-700', label: 'En préparation', icon: Package },
  ready: { color: 'bg-yellow-100 text-yellow-700', label: 'Prêt à expédier', icon: Clock },
  shipped: { color: 'bg-purple-100 text-purple-700', label: 'En transit', icon: Truck },
  out_for_delivery: { color: 'bg-orange-100 text-orange-700', label: 'En livraison', icon: Navigation },
  delivered: { color: 'bg-green-100 text-green-700', label: 'Livrée', icon: CheckCircle2 },
  failed: { color: 'bg-red-100 text-red-700', label: 'Échec', icon: AlertCircle },
};

// Carte livraison
function DeliveryCard({ delivery, onViewDetails, onMarkAsShipped }) {
  const status = DELIVERY_STATUSES[delivery.status] || DELIVERY_STATUSES.preparing;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-bold text-gray-900">#{delivery.orderId}</p>
          <p className="text-sm text-gray-500">{delivery.date}</p>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
          <StatusIcon size={12} />
          {status.label}
        </span>
      </div>

      {/* Destination */}
      <div className="flex items-start gap-2 mb-3 bg-gray-50 rounded-lg p-3">
        <MapPin size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-medium text-gray-900 text-sm">{delivery.customer}</p>
          <p className="text-xs text-gray-500">{delivery.address}</p>
          <p className="text-xs text-gray-500">{delivery.postalCode} {delivery.city}</p>
        </div>
      </div>

      {/* Items */}
      <div className="mb-3">
        <p className="text-sm text-gray-600">
          <span className="font-medium">{delivery.itemsCount} article(s)</span>
          {delivery.items && (
            <span className="text-gray-400"> - {delivery.items[0]?.title}</span>
          )}
        </p>
      </div>

      {/* Tracking */}
      {delivery.trackingNumber && (
        <div className="mb-3 p-2 bg-blue-50 rounded-lg">
          <p className="text-xs text-blue-600">
            N° suivi: <span className="font-mono font-medium">{delivery.trackingNumber}</span>
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-3 border-t border-gray-100">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onViewDetails(delivery)}
          className="flex-1 rounded-lg"
        >
          <Eye size={14} className="mr-1" /> Détails
        </Button>
        {delivery.status === 'preparing' && (
          <Button
            size="sm"
            onClick={() => onMarkAsShipped(delivery)}
            className="flex-1 bg-purple-500 hover:bg-purple-600 rounded-lg"
          >
            <Truck size={14} className="mr-1" /> Expédier
          </Button>
        )}
        {delivery.status === 'ready' && (
          <Button
            size="sm"
            onClick={() => onMarkAsShipped(delivery)}
            className="flex-1 bg-purple-500 hover:bg-purple-600 rounded-lg"
          >
            <Truck size={14} className="mr-1" /> Confirmer envoi
          </Button>
        )}
      </div>
    </motion.div>
  );
}

// Modal d'expédition
function ShippingModal({ isOpen, onClose, delivery, onConfirm }) {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('colissimo');

  if (!isOpen || !delivery) return null;

  const handleSubmit = () => {
    if (!trackingNumber.trim()) {
      toast.error('Veuillez entrer un numéro de suivi');
      return;
    }
    onConfirm(delivery, { trackingNumber, carrier });
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Confirmer l'expédition</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Transporteur</label>
            <select
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl"
            >
              <option value="colissimo">Colissimo</option>
              <option value="chronopost">Chronopost</option>
              <option value="mondialrelay">Mondial Relay</option>
              <option value="ups">UPS</option>
              <option value="dhl">DHL</option>
              <option value="fedex">FedEx</option>
              <option value="other">Autre</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de suivi</label>
            <Input
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="Ex: 6A12345678901"
              className="rounded-xl"
            />
          </div>

          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-sm text-gray-600">
              <span className="font-medium">Commande:</span> #{delivery.orderId}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Client:</span> {delivery.customer}
            </p>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl">
            Annuler
          </Button>
          <Button onClick={handleSubmit} className="flex-1 bg-purple-500 hover:bg-purple-600 rounded-xl">
            <Truck size={16} className="mr-2" />
            Confirmer
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function LivraisonsPage() {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [shippingModal, setShippingModal] = useState({ isOpen: false, delivery: null });

  useEffect(() => {
    fetchDeliveries();
  }, []);

  const fetchDeliveries = async () => {
    setLoading(true);
    try {
      // Données simulées
      setDeliveries([
        {
          id: 'LIV-001',
          orderId: 'CMD-2024-001',
          date: '15 Jan 2025',
          status: 'preparing',
          customer: 'Marie Lambert',
          address: '12 rue de la Paix',
          postalCode: '75002',
          city: 'Paris',
          phone: '06 12 34 56 78',
          itemsCount: 1,
          items: [{ title: 'Perceuse Bosch Professional' }],
          trackingNumber: null
        },
        {
          id: 'LIV-002',
          orderId: 'CMD-2024-002',
          date: '14 Jan 2025',
          status: 'ready',
          customer: 'Pierre Durand',
          address: '45 avenue Victor Hugo',
          postalCode: '69003',
          city: 'Lyon',
          phone: '06 98 76 54 32',
          itemsCount: 2,
          items: [{ title: 'Lot de 50 chevilles' }],
          trackingNumber: null
        },
        {
          id: 'LIV-003',
          orderId: 'CMD-2024-003',
          date: '13 Jan 2025',
          status: 'shipped',
          customer: 'Sophie Martin',
          address: '8 place Bellecour',
          postalCode: '69002',
          city: 'Lyon',
          phone: '06 55 44 33 22',
          itemsCount: 1,
          items: [{ title: 'Niveau laser Makita' }],
          trackingNumber: '6A12345678901'
        },
        {
          id: 'LIV-004',
          orderId: 'CMD-2024-004',
          date: '12 Jan 2025',
          status: 'out_for_delivery',
          customer: 'Jean Petit',
          address: '23 rue Gambetta',
          postalCode: '33000',
          city: 'Bordeaux',
          phone: '06 11 22 33 44',
          itemsCount: 1,
          items: [{ title: 'Coffret tournevis Facom' }],
          trackingNumber: '6A98765432109'
        },
        {
          id: 'LIV-005',
          orderId: 'CMD-2024-005',
          date: '10 Jan 2025',
          status: 'delivered',
          customer: 'Lucie Bernard',
          address: '5 allée des Roses',
          postalCode: '31000',
          city: 'Toulouse',
          phone: '06 77 88 99 00',
          itemsCount: 3,
          items: [{ title: 'Marteau Peugeot 500g' }],
          trackingNumber: '6A11223344556'
        },
      ]);
    } catch (e) {
      console.error('Error fetching deliveries:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsShipped = (delivery, shippingInfo) => {
    setDeliveries(deliveries.map(d => 
      d.id === delivery.id 
        ? { ...d, status: 'shipped', trackingNumber: shippingInfo?.trackingNumber || d.trackingNumber }
        : d
    ));
    toast.success(`Livraison ${delivery.orderId} marquée comme expédiée`);
  };

  const filteredDeliveries = deliveries.filter(d => {
    const matchesSearch = d.orderId.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          d.customer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || d.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    preparing: deliveries.filter(d => d.status === 'preparing').length,
    ready: deliveries.filter(d => d.status === 'ready').length,
    inTransit: deliveries.filter(d => d.status === 'shipped' || d.status === 'out_for_delivery').length,
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="livraisons-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Livraisons</h1>
        <p className="text-gray-500">Suis et gère tes expéditions</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Package size={24} className="text-blue-600" />
            <div>
              <p className="text-2xl font-bold text-blue-700">{stats.preparing}</p>
              <p className="text-sm text-blue-600">En préparation</p>
            </div>
          </div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Clock size={24} className="text-yellow-600" />
            <div>
              <p className="text-2xl font-bold text-yellow-700">{stats.ready}</p>
              <p className="text-sm text-yellow-600">Prêt à expédier</p>
            </div>
          </div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Truck size={24} className="text-purple-600" />
            <div>
              <p className="text-2xl font-bold text-purple-700">{stats.inTransit}</p>
              <p className="text-sm text-purple-600">En transit</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Rechercher une livraison..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-xl"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-xl bg-white text-sm"
        >
          <option value="all">Tous les statuts</option>
          <option value="preparing">En préparation</option>
          <option value="ready">Prêt à expédier</option>
          <option value="shipped">En transit</option>
          <option value="out_for_delivery">En livraison</option>
          <option value="delivered">Livrées</option>
          <option value="failed">Échec</option>
        </select>
      </div>

      {/* Deliveries List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
        </div>
      ) : filteredDeliveries.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <Truck size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="font-bold text-gray-900 mb-2">Aucune livraison</h3>
          <p className="text-gray-500">Tes livraisons apparaîtront ici</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filteredDeliveries.map((delivery) => (
            <DeliveryCard
              key={delivery.id}
              delivery={delivery}
              onViewDetails={(d) => console.log('View details:', d)}
              onMarkAsShipped={(d) => setShippingModal({ isOpen: true, delivery: d })}
            />
          ))}
        </div>
      )}

      {/* Shipping Modal */}
      <ShippingModal
        isOpen={shippingModal.isOpen}
        onClose={() => setShippingModal({ isOpen: false, delivery: null })}
        delivery={shippingModal.delivery}
        onConfirm={handleMarkAsShipped}
      />
    </div>
  );
}
